<template>
  <div class="modal-overlay">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="modal-header-content">
          <h2>{{ modalTitle }}</h2>
          <span v-if="form.internal_code" class="internal-code-badge">
            {{ form.internal_code }}
          </span>
        </div>
        <div class="modal-header-actions">
          <!-- Step through the list without closing. Hidden while creating, and in
               the places that open this modal standalone without a list to walk. -->
          <template v-if="accountCode && (canGoPrevious || canGoNext)">
            <button
              type="button"
              class="btn-nav"
              :disabled="!canGoPrevious"
              :title="$t('accountCodes.previousCode')"
              @click="$emit('navigate', -1)"
            >
              <LineIcon name="chevron-left" :size="15" :stroke-width="1.8" />
            </button>
            <button
              type="button"
              class="btn-nav"
              :disabled="!canGoNext"
              :title="$t('accountCodes.nextCode')"
              @click="$emit('navigate', 1)"
            >
              <LineIcon name="chevron-right" :size="15" :stroke-width="1.8" />
            </button>
          </template>
          <button @click="$emit('close')" class="btn-close">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div v-if="mappingMode">
          <!-- Mapping Mode: Show barcode first, then select account code -->
          <div class="form-group">
            <label for="barcode_mapping">Barcode <span class="required">*</span></label>
            <input
              id="barcode_mapping"
              v-model="form.barcode_mapping"
              type="text"
              placeholder="e.g., 0002, 0001, 0011"
              required
            />
            <p class="field-hint">{{ $t('accountCodes.barcodeEnterHint') }}</p>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="account_code">Account Code <span class="required">*</span></label>
              <input
                id="account_code"
                v-model="form.account_code"
                type="text"
                placeholder="e.g., 701/1"
                required
              />
            </div>

            <div class="form-group">
              <label for="account_type">Account Type</label>
              <SelectField v-model="form.account_type" :options="accounttypeOptions" id="account_type" />
            </div>
          </div>

          <div class="form-group">
            <label for="internal_code">{{ $t('accountCodes.internalCode') }}</label>
            <input
              id="internal_code"
              v-model="form.internal_code"
              type="text"
              placeholder="e.g., IC-001"
            />
          </div>

          <div class="form-group">
            <label for="account_name">Description <span class="required">*</span></label>
            <input
              id="account_name"
              v-model="form.account_name"
              type="text"
              placeholder="e.g., Diesel Fuel Expense"
              required
            />
          </div>
        </div>

        <div v-else>
          <!-- Normal Mode: Original fields -->
        <div class="form-row">
          <div class="form-group">
            <label for="account_code">{{ $t('modals.accountCode') }} <span class="required">*</span></label>
            <input
              id="account_code"
              v-model="form.account_code"
              type="text"
              :placeholder="$t('modals.accountCodePlaceholder')"
              required
            />
          </div>

          <div class="form-group">
            <label for="internal_code">{{ $t('accountCodes.internalCode') }}</label>
            <input
              id="internal_code"
              v-model="form.internal_code"
              type="text"
              placeholder="e.g., IC-001"
            />
          </div>

          <div class="form-group">
            <label for="account_type">{{ $t('modals.accountType') }} <span class="required">*</span></label>
            <SelectField v-model="form.account_type" :options="accounttype2Options" id="account_type" />
          </div>
        </div>

        <div class="form-group">
          <label for="account_name">{{ $t('modals.accountName') }} <span class="required">*</span></label>
          <input
            id="account_name"
            v-model="form.account_name"
            type="text"
            :placeholder="$t('modals.accountNamePlaceholder')"
            required
          />
        </div>

        <div class="form-group">
          <label for="category">{{ $t('accountCodes.category') }}</label>
          <input
            id="category"
            v-model="form.category"
            type="text"
            :placeholder="$t('modals.categoryPlaceholder')"
          />
        </div>

        <!-- Only meaningful while the Santa pack-size conversion is switched on
             in Companies → Settings; otherwise the factor is never read. -->
        <div v-if="authStore.useQuantityMultiplier" class="form-group">
          <label for="quantity_multiplier">{{ $t('accountCodes.quantityMultiplier') }}</label>
          <input
            id="quantity_multiplier"
            v-model="form.quantity_multiplier"
            type="number"
            step="0.0001"
            min="0.0001"
            placeholder="1"
          />
          <p class="field-hint">{{ $t('accountCodes.quantityMultiplierHint') }}</p>
        </div>

        <div class="form-group">
          <label for="barcode_mapping">{{ $t('accountCodes.barcodeMappingLabel') }}</label>
          <div class="input-with-button">
            <input
              id="barcode_mapping"
              v-model="form.barcode_mapping"
              type="text"
              placeholder="e.g., 0002, 0001, 0011"
            />
            <button 
              type="button" 
              class="btn-icon"
              @click="viewAllBarcodes"
              :disabled="!accountCode || !accountCode.id"
              :title="accountCode && accountCode.id
                ? $t('accountCodes.manageBarcodes')
                : $t('accountCodes.availableAfterSave')"
            >
              <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
            </button>
          </div>
          <p class="field-hint">{{ $t('accountCodes.barcodeMappingHint') }}</p>
        </div>

        <div class="form-group">
          <label for="description_mapping">{{ $t('accountCodes.descriptionMappingLabel') }}</label>
          <div class="input-with-button">
            <input
              id="description_mapping"
              v-model="form.description_mapping"
              type="text"
              placeholder="e.g., ДИЗЕЛОВО ГОРИВО, Diesel, Fuel"
            />
            <button
              type="button"
              class="btn-icon"
              @click="viewAllDescriptions"
              :disabled="!accountCode || !accountCode.id"
              :title="accountCode && accountCode.id
                ? $t('accountCodes.manageDescriptions')
                : $t('accountCodes.availableAfterSave')"
            >
              <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
            </button>
          </div>
          <div
            class="form-group-toggle form-group-toggle-inline"
            :title="$t('accountCodes.looseMatchHint', 'Когато е включено, всяка дума от ключа поотделно ще пасне на описанието. Изключено = точен подниз.')"
          >
            <label class="toggle">
              <input
                type="checkbox"
                :checked="form.description_match_mode === 'any_word'"
                @change="form.description_match_mode = $event.target.checked ? 'any_word' : 'substring'"
              />
              <span class="toggle-track"></span>
            </label>
            <span class="toggle-text">{{ $t('accountCodes.looseMatch', 'По думи') }}</span>
          </div>
          <p class="field-hint">{{ $t('accountCodes.descriptionMappingHint') }}</p>
        </div>

        <div class="form-group form-group-toggle">
          <label class="toggle">
            <input type="checkbox" v-model="form.is_active" />
            <span class="toggle-track"></span>
          </label>
          <span class="toggle-text">{{ $t('modals.active') }}</span>
        </div>

        <div class="form-group form-group-toggle">
          <label class="toggle">
            <input type="checkbox" v-model="form.is_default" />
            <span class="toggle-track"></span>
          </label>
          <span class="toggle-text">{{ $t('accountCodes.defaultAccountCode') }}</span>
        </div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            {{ $t('modals.cancel') }}
          </button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saveButtonText }}
          </button>
        </div>
      </form>
    </div>
    
    <!-- Barcodes Modal -->
    <div v-if="showBarcodesModal" class="descriptions-modal-overlay">
      <div class="descriptions-modal-content" @click.stop>
        <div class="descriptions-modal-header">
          <h3>All Barcodes for "{{ currentAccountCode }}"</h3>
          <button @click="closeBarcodesModal" class="btn-close">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
        <div class="descriptions-modal-body">
          <!-- Add New Barcode Section -->
          <div class="add-description-section">
            <label for="new-barcode" class="add-description-label">Add New Barcode</label>
            <div class="add-description-input-group">
              <input
                id="new-barcode"
                v-model="newBarcode"
                type="text"
                placeholder="Enter new barcode..."
                class="add-description-input"
                @keyup.enter="addNewBarcode"
              />
              <button 
                @click="addNewBarcode" 
                class="btn-add"
                :disabled="!newBarcode.trim() || addingBarcode"
              >
                <LineIcon name="plus" :size="15" :stroke-width="1.8" v-if="!addingBarcode" />
                <span v-if="addingBarcode">Adding...</span>
                <span v-else>Add</span>
              </button>
            </div>
          </div>

          <!-- Existing Barcodes -->
          <div class="descriptions-header">
            <div class="descriptions-select-all">
              <input 
                type="checkbox" 
                id="select-all-barcodes"
                :checked="allBarcodesSelected"
                @change="toggleSelectAllBarcodes"
                class="description-checkbox"
              />
              <label for="select-all-barcodes" class="select-all-label">
                Select All
              </label>
              <span class="descriptions-count-inline">
                ({{ allBarcodes.length }} {{ allBarcodes.length === 1 ? 'barcode' : 'barcodes' }})
              </span>
            </div>
            <div v-if="selectedBarcodes.length > 0" class="descriptions-actions">
              <button @click="deleteSelectedBarcodes" class="btn-delete-selected" :disabled="deletingBarcodes">
                <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                Delete Selected ({{ selectedBarcodes.length }})
              </button>
            </div>
          </div>
          <div class="descriptions-list">
            <div v-for="(barcode, index) in allBarcodes" :key="index" class="description-item">
              <input 
                type="checkbox" 
                :value="barcode" 
                v-model="selectedBarcodes"
                class="description-checkbox"
                :disabled="editingBarcodeIndex === index"
              />
              <span class="description-number">{{ index + 1 }}.</span>
              <input 
                v-if="editingBarcodeIndex === index"
                v-model="editingBarcodeText"
                type="text"
                class="description-edit-input"
                @keyup.enter="saveBarcodeEdit(index, barcode)"
                @keyup.esc="cancelBarcodeEdit"
                @blur="saveBarcodeEdit(index, barcode)"
                ref="editBarcodeInput"
              />
              <span 
                v-else
                class="description-text" 
                @dblclick="startBarcodeEdit(index, barcode)"
                :title="'Double-click to edit'"
              >
                {{ barcode }}
              </span>
              <input 
                v-if="editingBarcodeIndex !== index"
                type="text"
                :value="getInternalCodeForBarcode(barcode)"
                @change="updateBarcodeInternalCode(barcode, $event.target.value)"
                placeholder="Internal Code"
                class="internal-code-input"
                title="Enter internal code for this barcode"
              />
              <div v-if="editingBarcodeIndex === index" class="edit-actions">
                <button @click="saveBarcodeEdit(index, barcode)" class="btn-save-edit" :disabled="savingBarcodeEdit">
                  <LineIcon name="check" :size="15" :stroke-width="1.8" />
                </button>
                <button @click="cancelBarcodeEdit" class="btn-cancel-edit">
                  <LineIcon name="x" :size="15" :stroke-width="1.8" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="descriptions-modal-footer">
          <button @click="closeBarcodesModal" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Descriptions Modal -->
    <div v-if="showDescriptionsModal" class="descriptions-modal-overlay">
      <div class="descriptions-modal-content" @click.stop>
        <div class="descriptions-modal-header">
          <h3>All Descriptions for "{{ currentAccountCode }}"</h3>
          <button @click="closeDescriptionsModal" class="btn-close">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
        <div class="descriptions-modal-body">
          <!-- Add New Description Section -->
          <div class="add-description-section">
            <label for="new-description" class="add-description-label">Add New Description</label>
            <div class="add-description-input-group">
              <input
                id="new-description"
                v-model="newDescription"
                type="text"
                placeholder="Enter new product description..."
                class="add-description-input"
                @keyup.enter="addNewDescription"
              />
              <button 
                @click="addNewDescription" 
                class="btn-add"
                :disabled="!newDescription.trim() || addingDescription"
              >
                <LineIcon name="plus" :size="15" :stroke-width="1.8" v-if="!addingDescription" />
                <span v-if="addingDescription">Adding...</span>
                <span v-else>Add</span>
              </button>
            </div>
          </div>

          <!-- Existing Descriptions -->
          <div class="descriptions-header">
            <div class="descriptions-select-all">
              <input 
                type="checkbox" 
                id="select-all-descriptions"
                :checked="allDescriptionsSelected"
                @change="toggleSelectAll"
                class="description-checkbox"
              />
              <label for="select-all-descriptions" class="select-all-label">
                Select All
              </label>
              <span class="descriptions-count-inline">
                ({{ allDescriptions.length }} {{ allDescriptions.length === 1 ? 'description' : 'descriptions' }})
              </span>
            </div>
            <div v-if="selectedDescriptions.length > 0" class="descriptions-actions">
              <button @click="deleteSelectedDescriptions" class="btn-delete-selected" :disabled="deletingDescriptions">
                <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                Delete Selected ({{ selectedDescriptions.length }})
              </button>
            </div>
          </div>
          <div class="descriptions-list">
            <div v-for="(description, index) in allDescriptions" :key="index" class="description-item">
              <input 
                type="checkbox" 
                :value="description" 
                v-model="selectedDescriptions"
                class="description-checkbox"
                :disabled="editingIndex === index"
              />
              <span class="description-number">{{ index + 1 }}.</span>
              <input 
                v-if="editingIndex === index"
                v-model="editingText"
                type="text"
                class="description-edit-input"
                @keyup.enter="saveDescriptionEdit(index, description)"
                @keyup.esc="cancelEdit"
                @blur="saveDescriptionEdit(index, description)"
                ref="editInput"
              />
              <span 
                v-else
                class="description-text" 
                @dblclick="startEdit(index, description)"
                :title="'Double-click to edit'"
              >
                {{ description }}
              </span>
              <input
                v-if="editingIndex !== index"
                type="text"
                :value="getInternalCodeForDescription(description)"
                @change="updateDescriptionInternalCode(description, $event.target.value)"
                placeholder="Internal Code"
                class="internal-code-input"
                title="Enter internal code for this description"
              />
              <div
                v-if="editingIndex !== index"
                class="form-group-toggle form-group-toggle-row"
                :title="$t('accountCodes.looseMatchHint', 'Когато е включено, всяка дума от ключа поотделно ще пасне на описанието. Изключено = точен подниз.')"
              >
                <label class="toggle">
                  <input
                    type="checkbox"
                    :checked="getMatchModeForDescription(description) === 'any_word'"
                    @change="updateDescriptionMatchMode(description, $event.target.checked ? 'any_word' : 'substring')"
                  />
                  <span class="toggle-track"></span>
                </label>
                <span class="toggle-text">{{ $t('accountCodes.looseMatch', 'По думи') }}</span>
              </div>
              <div v-if="editingIndex === index" class="edit-actions">
                <button @click="saveDescriptionEdit(index, description)" class="btn-save-edit" :disabled="savingEdit">
                  <LineIcon name="check" :size="15" :stroke-width="1.8" />
                </button>
                <button @click="cancelEdit" class="btn-cancel-edit">
                  <LineIcon name="x" :size="15" :stroke-width="1.8" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="descriptions-modal-footer">
          <button @click="closeDescriptionsModal" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import SelectField from '@/components/SelectField.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, watch, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

const { t } = useI18n();
const authStore = useAuthStore();

const props = defineProps({
  accountCode: {
    type: Object,
    default: null
  },
  companyId: {
    type: [String, Number],
    required: true
  },
  mappingMode: {
    type: Boolean,
    default: false
  },
  prefilledData: {
    type: Object,
    default: null
  },
  canGoPrevious: {
    type: Boolean,
    default: false
  },
  canGoNext: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'saved', 'navigate']);

const modalTitle = computed(() => {
  if (props.mappingMode) {
    return props.accountCode ? 'Edit Code Mapping' : 'Add Code Mapping';
  }
  return props.accountCode ? t('modals.editAccountCode') : t('modals.addAccountCode');
});

const saveButtonText = computed(() => {
  return saving.value ? t('modals.saving') : t('modals.saveAccountCode');
});

const allDescriptionsSelected = computed(() => {
  return allDescriptions.value.length > 0 && 
         selectedDescriptions.value.length === allDescriptions.value.length;
});

const allBarcodesSelected = computed(() => {
  return allBarcodes.value.length > 0 && 
         selectedBarcodes.value.length === allBarcodes.value.length;
});

const form = ref({
  account_code: '',
  account_name: '',
  account_type: '',
  internal_code: '',
  category: '',
  quantity_multiplier: 1,
  barcode_mapping: '',
  description_mapping: '',
  description_match_mode: 'substring',
  barcode_internal_code_mapping: {},
  description_internal_code_mapping: {},
  is_active: true,
  is_default: false
});

const saving = ref(false);
const initialDescriptionMatchMode = ref('substring');

// Description modal state
const showDescriptionsModal = ref(false);
const allDescriptions = ref([]);
const currentAccountCode = ref('');
const newDescription = ref('');
const addingDescription = ref(false);
const selectedDescriptions = ref([]);
const deletingDescriptions = ref(false);
const descriptionEntries = ref([]); // Store full entries with IDs
const editingIndex = ref(null);
const editingText = ref('');
const savingEdit = ref(false);

// Barcode modal state
const showBarcodesModal = ref(false);
const allBarcodes = ref([]);
const currentInternalCode = ref('');
const newBarcode = ref('');
const addingBarcode = ref(false);
const selectedBarcodes = ref([]);
const deletingBarcodes = ref(false);
const barcodeEntries = ref([]); // Store full entries with IDs
const editingBarcodeIndex = ref(null);
const editingBarcodeText = ref('');
const savingBarcodeEdit = ref(false);

// Load account code data if editing
watch(() => props.accountCode, (accountCode) => {
  console.log('AccountCodeModal - props.accountCode changed:', accountCode);
  if (accountCode) {
    form.value = { ...accountCode };
    console.log('Form initialized with ID:', form.value.id);

    // Records created before the pack-size factor existed come back without one.
    form.value.quantity_multiplier = Number(form.value.quantity_multiplier) > 0
      ? Number(form.value.quantity_multiplier)
      : 1;
    
    // Convert barcode_mapping array to comma-separated string for display
    if (Array.isArray(form.value.barcode_mapping)) {
      form.value.barcode_mapping = form.value.barcode_mapping.join(', ');
    } else if (!form.value.barcode_mapping) {
      form.value.barcode_mapping = '';
    }
    
    // Convert description_mapping array to comma-separated string for display
    if (Array.isArray(form.value.description_mapping)) {
      form.value.description_mapping = form.value.description_mapping.join(', ');
    } else if (!form.value.description_mapping) {
      form.value.description_mapping = '';
    }

    // Default match mode from existing entries: if any keyword is "any_word", show toggle ON.
    const entries = accountCode.descriptionMappings || accountCode.description_mappings || [];
    if (Array.isArray(entries) && entries.length > 0) {
      const hasLoose = entries.some(e => e.match_mode === 'any_word');
      form.value.description_match_mode = hasLoose ? 'any_word' : 'substring';
    } else {
      form.value.description_match_mode = 'substring';
    }
    // Remember the initial value so we only push a bulk override when the user
    // actually flips the toggle (otherwise per-keyword modes set in the sub-modal
    // would be silently overwritten on every save).
    initialDescriptionMatchMode.value = form.value.description_match_mode;
  } else {
    // Reset form when creating new
    form.value = {
      account_code: '',
      account_name: '',
      account_type: '',
      internal_code: '',
      category: '',
      quantity_multiplier: 1,
      barcode_mapping: '',
      description_mapping: '',
      description_match_mode: 'substring',
      barcode_internal_code_mapping: {},
      description_internal_code_mapping: {},
      is_active: true,
      is_default: false
    };
    console.log('Form reset for new account code');
  }
}, { immediate: true });

// Watch for prefilledData to populate form fields
watch(() => props.prefilledData, (data) => {
  if (data && !props.accountCode) {
    // Only apply prefilled data when creating new account code (not editing)
    if (data.barcode_mapping && Array.isArray(data.barcode_mapping)) {
      form.value.barcode_mapping = data.barcode_mapping.join(', ');
    }
    if (data.description_mapping && Array.isArray(data.description_mapping)) {
      form.value.description_mapping = data.description_mapping.join(', ');
    }
    if (data.account_name) {
      form.value.account_name = data.account_name;
    }
  }
}, { immediate: true });

const handleSubmit = async () => {
  try {
    saving.value = true;

    // Prepare the data to send
    const dataToSend = { ...form.value };
    
    // Convert barcode_mapping from comma-separated string to array
    if (dataToSend.barcode_mapping) {
      if (typeof dataToSend.barcode_mapping === 'string') {
        dataToSend.barcode_mapping = dataToSend.barcode_mapping
          .split(',')
          .map(code => code.trim().replace(/,/g, '.'))
          .filter(code => code.length > 0);
      }
    } else {
      // If empty, send as empty array
      dataToSend.barcode_mapping = [];
    }
    
    // Convert description_mapping from comma-separated string to array
    if (dataToSend.description_mapping) {
      if (typeof dataToSend.description_mapping === 'string') {
        dataToSend.description_mapping = dataToSend.description_mapping
          .split(',')
          .map(desc => desc.trim().replace(/,/g, '.'))
          .filter(desc => desc.length > 0);
      }
    } else {
      // If empty, send as empty array
      dataToSend.description_mapping = [];
    }

    // Only push a bulk match_mode override when the user actually flipped the
    // toggle on this form. Otherwise omit it so the backend preserves any
    // per-keyword modes set via the sub-modal.
    if (dataToSend.description_match_mode === initialDescriptionMatchMode.value) {
      delete dataToSend.description_match_mode;
    }

    console.log('Account Code Modal Submit:', {
      isUpdate: !!props.accountCode,
      accountCodeId: props.accountCode?.id,
      formValueId: form.value.id,
      dataToSend
    });

    // CRITICAL: Use form.value.id to determine if it's an update
    // This ensures we're checking the actual ID from the loaded data
    let response;
    if (form.value.id) {
      // Update existing account code
      console.log('Updating account code with ID:', form.value.id);
      response = await api.put(`/api/companies/${props.companyId}/account-codes/${form.value.id}`, dataToSend);
    } else {
      // Create new account code
      console.log('Creating new account code');
      response = await api.post(`/api/companies/${props.companyId}/account-codes`, dataToSend);
    }

    console.log('AccountCodeModal - Emitting saved with data:', response.data);
    emit('saved', response.data);
  } catch (error) {
    console.error('Failed to save account code:', error);
    const errorMessage = error.response?.data?.message || 'Failed to save account code. Please try again.';
    alert(errorMessage);
  } finally {
    saving.value = false;
  }
};

const handleOverlayClick = () => {
  emit('close');
};

const viewAllDescriptions = async () => {
  if (!props.accountCode?.id) {
    return;
  }

  try {
    // Fetch description mappings from dedicated API
    const response = await api.get(`/api/account-codes/${props.accountCode.id}/description-mappings`);
    const mappings = response.data.data;
    
    // Store entries with IDs for editing/deletion
    descriptionEntries.value = mappings;
    
    if (mappings.length === 0) {
      alert('No descriptions found for this account code.');
      return;
    }
    
    // Extract values for display
    allDescriptions.value = mappings.map(m => m.value).sort();
    currentAccountCode.value = props.accountCode.account_code;
    selectedDescriptions.value = [];
    showDescriptionsModal.value = true;
    
  } catch (error) {
    console.error('Failed to fetch descriptions:', error);
    alert('Failed to load descriptions. Please try again.');
  }
};

const getInternalCodeForDescription = (description) => {
  const entry = descriptionEntries.value.find(e => e.value === description);
  return entry?.internal_code || '';
};

const getInternalCodeForBarcode = (barcode) => {
  const entry = barcodeEntries.value.find(e => e.value === barcode);
  return entry?.internal_code || '';
};

const updateBarcodeInternalCode = async (barcode, internalCode) => {
  try {
    const entry = barcodeEntries.value.find(e => e.value === barcode);
    if (!entry) return;

    await api.put(`/api/barcode-mappings/${entry.id}`, {
      value: entry.value,
      internal_code: internalCode.trim() || null,
    });

    entry.internal_code = internalCode.trim() || null;
    emit('saved');

  } catch (error) {
    console.error('Failed to update barcode internal code:', error);
    alert('Failed to update internal code. Please try again.');
  }
};

const updateDescriptionInternalCode = async (description, internalCode) => {
  try {
    const entry = descriptionEntries.value.find(e => e.value === description);
    if (!entry) return;

    await api.put(`/api/description-mappings/${entry.id}`, {
      value: entry.value,
      match_mode: entry.match_mode || 'substring',
      internal_code: internalCode.trim() || null,
    });

    entry.internal_code = internalCode.trim() || null;
    emit('saved');

  } catch (error) {
    console.error('Failed to update description internal code:', error);
    alert('Failed to update internal code. Please try again.');
  }
};

const getMatchModeForDescription = (description) => {
  const entry = descriptionEntries.value.find(e => e.value === description);
  return entry?.match_mode || 'substring';
};

const updateDescriptionMatchMode = async (description, mode) => {
  try {
    const entry = descriptionEntries.value.find(e => e.value === description);
    if (!entry) return;

    await api.put(`/api/description-mappings/${entry.id}`, {
      value: entry.value,
      match_mode: mode,
      internal_code: entry.internal_code || null,
    });

    entry.match_mode = mode;
    emit('saved');
  } catch (error) {
    console.error('Failed to update description match mode:', error);
    alert('Failed to update match mode. Please try again.');
  }
};

const closeDescriptionsModal = () => {
  showDescriptionsModal.value = false;
  allDescriptions.value = [];
  currentAccountCode.value = '';
  newDescription.value = '';
  addingDescription.value = false;
  selectedDescriptions.value = [];
  descriptionEntries.value = [];
  editingIndex.value = null;
  editingText.value = '';
};

const startEdit = (index, description) => {
  editingIndex.value = index;
  editingText.value = description;
  // Focus the input in next tick
  setTimeout(() => {
    const inputs = document.querySelectorAll('.description-edit-input');
    if (inputs[0]) inputs[0].focus();
  }, 10);
};

const cancelEdit = () => {
  editingIndex.value = null;
  editingText.value = '';
};

const saveDescriptionEdit = async (index, oldDescription) => {
  if (savingEdit.value || !editingText.value.trim() || editingText.value === oldDescription) {
    cancelEdit();
    return;
  }

  try {
    savingEdit.value = true;

    // Find the mapping entry by value
    const entry = descriptionEntries.value.find(e => e.value === oldDescription);
    if (!entry) {
      alert('Could not find entry to update.');
      cancelEdit();
      return;
    }

    // Update via dedicated API
    await api.put(`/api/description-mappings/${entry.id}`, {
      value: editingText.value.trim(),
      internal_code: entry.internal_code,
    });

    // Update local state
    entry.value = editingText.value.trim();
    allDescriptions.value[index] = editingText.value.trim();
    allDescriptions.value = [...allDescriptions.value].sort();

    emit('saved');
    cancelEdit();

  } catch (error) {
    console.error('Failed to update description:', error);
    alert('Failed to update description. Please try again.');
  } finally {
    savingEdit.value = false;
  }
};

const toggleSelectAll = () => {
  if (allDescriptionsSelected.value) {
    selectedDescriptions.value = [];
  } else {
    selectedDescriptions.value = [...allDescriptions.value];
  }
};

const deleteSelectedDescriptions = async () => {
  if (selectedDescriptions.value.length === 0 || deletingDescriptions.value) {
    return;
  }

  const count = selectedDescriptions.value.length;
  if (!confirm(`Are you sure you want to delete ${count} selected description${count !== 1 ? 's' : ''}?`)) {
    return;
  }

  try {
    deletingDescriptions.value = true;

    // Find IDs of selected description values
    const idsToDelete = descriptionEntries.value
      .filter(e => selectedDescriptions.value.includes(e.value))
      .map(e => e.id);

    await api.post('/api/description-mappings/bulk-delete', { ids: idsToDelete });

    // Update local state
    descriptionEntries.value = descriptionEntries.value.filter(e => !idsToDelete.includes(e.id));
    allDescriptions.value = allDescriptions.value.filter(desc => !selectedDescriptions.value.includes(desc));
    selectedDescriptions.value = [];

    emit('saved');

    if (allDescriptions.value.length === 0) {
      closeDescriptionsModal();
    }

  } catch (error) {
    console.error('Failed to delete descriptions:', error);
    alert('Failed to delete descriptions. Please try again.');
  } finally {
    deletingDescriptions.value = false;
  }
};

const addNewDescription = async () => {
  if (!newDescription.value.trim() || addingDescription.value) {
    return;
  }

  try {
    addingDescription.value = true;

    // Create via dedicated API
    const response = await api.post(`/api/account-codes/${props.accountCode.id}/description-mappings`, {
      value: newDescription.value.trim(),
    });

    // Add to local state
    descriptionEntries.value.push(response.data);
    allDescriptions.value.push(newDescription.value.trim());
    allDescriptions.value = [...new Set(allDescriptions.value)].sort();

    newDescription.value = '';
    emit('saved');

  } catch (error) {
    console.error('Failed to add description:', error);
    alert('Failed to add description. Please try again.');
  } finally {
    addingDescription.value = false;
  }
};

// Barcode modal functions
const viewAllBarcodes = async () => {
  if (!props.accountCode?.id) {
    return;
  }

  try {
    // Fetch barcode mappings from dedicated API
    const response = await api.get(`/api/account-codes/${props.accountCode.id}/barcode-mappings`);
    const mappings = response.data.data;
    
    // Store entries with IDs
    barcodeEntries.value = mappings;
    
    if (mappings.length === 0) {
      alert('No barcodes found for this account code.');
      return;
    }
    
    // Extract values for display
    allBarcodes.value = mappings.map(m => m.value).sort();
    currentAccountCode.value = props.accountCode.account_code;
    currentInternalCode.value = props.accountCode.internal_code || '';
    selectedBarcodes.value = [];
    showBarcodesModal.value = true;
    
  } catch (error) {
    console.error('Failed to fetch barcodes:', error);
    alert('Failed to load barcodes. Please try again.');
  }
};

const closeBarcodesModal = () => {
  showBarcodesModal.value = false;
  allBarcodes.value = [];
  currentInternalCode.value = '';
  newBarcode.value = '';
  addingBarcode.value = false;
  selectedBarcodes.value = [];
  barcodeEntries.value = [];
  editingBarcodeIndex.value = null;
  editingBarcodeText.value = '';
};

const startBarcodeEdit = (index, barcode) => {
  editingBarcodeIndex.value = index;
  editingBarcodeText.value = barcode;
  // Focus the input in next tick
  setTimeout(() => {
    const inputs = document.querySelectorAll('.description-edit-input');
    if (inputs[0]) inputs[0].focus();
  }, 10);
};

const cancelBarcodeEdit = () => {
  editingBarcodeIndex.value = null;
  editingBarcodeText.value = '';
};

const saveBarcodeEdit = async (index, oldBarcode) => {
  if (savingBarcodeEdit.value || !editingBarcodeText.value.trim() || editingBarcodeText.value === oldBarcode) {
    cancelBarcodeEdit();
    return;
  }

  try {
    savingBarcodeEdit.value = true;

    const entry = barcodeEntries.value.find(e => e.value === oldBarcode);
    if (!entry) {
      alert('Could not find entry to update.');
      cancelBarcodeEdit();
      return;
    }

    await api.put(`/api/barcode-mappings/${entry.id}`, {
      value: editingBarcodeText.value.trim(),
      internal_code: entry.internal_code,
    });

    entry.value = editingBarcodeText.value.trim();
    allBarcodes.value[index] = editingBarcodeText.value.trim();
    allBarcodes.value = [...allBarcodes.value].sort();

    emit('saved');
    cancelBarcodeEdit();

  } catch (error) {
    console.error('Failed to update barcode:', error);
    alert('Failed to update barcode. Please try again.');
  } finally {
    savingBarcodeEdit.value = false;
  }
};

const toggleSelectAllBarcodes = () => {
  if (allBarcodesSelected.value) {
    selectedBarcodes.value = [];
  } else {
    selectedBarcodes.value = [...allBarcodes.value];
  }
};

const deleteSelectedBarcodes = async () => {
  if (selectedBarcodes.value.length === 0 || deletingBarcodes.value) {
    return;
  }

  const count = selectedBarcodes.value.length;
  if (!confirm(`Are you sure you want to delete ${count} selected barcode${count !== 1 ? 's' : ''}?`)) {
    return;
  }

  try {
    deletingBarcodes.value = true;

    const idsToDelete = barcodeEntries.value
      .filter(e => selectedBarcodes.value.includes(e.value))
      .map(e => e.id);

    await api.post('/api/barcode-mappings/bulk-delete', { ids: idsToDelete });

    barcodeEntries.value = barcodeEntries.value.filter(e => !idsToDelete.includes(e.id));
    allBarcodes.value = allBarcodes.value.filter(code => !selectedBarcodes.value.includes(code));
    selectedBarcodes.value = [];

    emit('saved');

    if (allBarcodes.value.length === 0) {
      closeBarcodesModal();
    }

  } catch (error) {
    console.error('Failed to delete barcodes:', error);
    alert('Failed to delete barcodes. Please try again.');
  } finally {
    deletingBarcodes.value = false;
  }
};

const addNewBarcode = async () => {
  if (!newBarcode.value.trim() || addingBarcode.value) {
    return;
  }

  try {
    addingBarcode.value = true;

    const response = await api.post(`/api/account-codes/${props.accountCode.id}/barcode-mappings`, {
      value: newBarcode.value.trim(),
    });

    barcodeEntries.value.push(response.data);
    allBarcodes.value.push(newBarcode.value.trim());
    allBarcodes.value = [...new Set(allBarcodes.value)].sort();

    newBarcode.value = '';
    emit('saved');

  } catch (error) {
    console.error('Failed to add barcode:', error);
    alert('Failed to add barcode. Please try again.');
  } finally {
    addingBarcode.value = false;
  }
};

/* What each field offers, as a list: the fields are the app's own
   dropdowns now, so the options are data rather than tags. */
const accounttypeOptions = computed(() => [
  { value: '', label: 'Select Type' },
  { value: 'expense', label: 'Expense' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
]);
const accounttype2Options = computed(() => [
  { value: '', label: t('modals.selectType') },
  { value: 'expense', label: t('modals.typeExpense') },
  { value: 'revenue', label: t('modals.typeRevenue') },
  { value: 'neutral', label: t('modals.typeNeutral') },
]);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-nav {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-nav:hover:not(:disabled) {
  background: var(--bg-secondary, #f3f4f6);
  color: var(--text-primary);
}

.btn-nav:disabled {
  opacity: 0.3;
  cursor: default;
}

.btn-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: var(--danger);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-light);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;

}

.field-hint {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
  font-style: italic;
}

.input-with-button {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.input-with-button input {
  flex: 1;
}

.input-with-button .btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.input-with-button .btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.input-with-button .btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-with-button .btn-icon:disabled:hover {
  background: var(--bg-gray);
  border-color: var(--border);
  color: var(--text-secondary);
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.btn-secondary,
.btn-primary {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-gray);
}

.btn-primary {
  background: var(--primary);
  border: none;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Descriptions Modal Styles */
.descriptions-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.descriptions-modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.descriptions-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.descriptions-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.descriptions-modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.add-description-section {
  margin-bottom: 24px;
  padding: 16px;
}

.add-description-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.add-description-input-group {
  display: flex;
  gap: 8px;
}

.add-description-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: var(--text-primary);
  transition: border-color 0.2s ease;
}

.add-description-input:focus {
  outline: none;
  border-color: var(--primary);
}

.descriptions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
}

.descriptions-select-all {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--bg-gray);
  border-radius: 8px;
  flex: 1;
}

.select-all-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.descriptions-count-inline {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.descriptions-count {
  padding: 12px;
  background: var(--bg-gray);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
  flex: 1;
}

.descriptions-actions {
  display: flex;
  gap: 8px;
}

.btn-delete-selected {
  padding: 10px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.btn-delete-selected:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
}

.btn-delete-selected:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.descriptions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.description-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: all 0.2s ease;
  align-items: center;
}

.description-item:hover {
  background: var(--bg-gray);
  border-color: var(--primary);
}

.description-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.description-number {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 14px;
  min-width: 30px;
}

.description-text {
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  flex: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.description-text:hover {
  background-color: #f3f4f6;
}

.internal-code-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  margin-left: 8px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.internal-code-badge-small {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  white-space: nowrap;
  margin-left: auto;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(102, 126, 234, 0.3);
}

.internal-code-input {
  width: 120px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-primary);
  background: #f9fafb;
  transition: all 0.2s ease;
  margin-left: auto;
  flex-shrink: 0;
}

/* iOS-style toggle for Active / Default Account Code form rows */
.form-group-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  margin: 0;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.2s ease;
  cursor: pointer;
}

.toggle-track::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle input:checked + .toggle-track {
  background: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.toggle input:focus-visible + .toggle-track {
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

/* Loose-match toggle below the Description Mapping field — sits under the input */
.form-group-toggle-inline {
  margin-top: 10px;
}

/* Loose-match toggle inline with each description list row */
.form-group-toggle-row {
  flex-shrink: 0;
}

.form-group-toggle-row .toggle-text {
  font-size: 12px;
  color: var(--text-secondary);
}

.internal-code-input:focus {
  outline: none;
  border-color: var(--primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.internal-code-input::placeholder {
  color: #9ca3af;
  font-size: 11px;
}

.description-edit-input {
  flex: 1;
  padding: 6px 10px;
  border: 2px solid var(--primary);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  outline: none;
  background: white;
}

.edit-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.btn-save-edit,
.btn-cancel-edit {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save-edit {
  background: #10b981;
  color: white;
}

.btn-save-edit:hover:not(:disabled) {
  background: #059669;
  transform: scale(1.05);
}

.btn-save-edit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel-edit {
  background: #ef4444;
  color: white;
}

.btn-cancel-edit:hover {
  background: #dc2626;
  transform: scale(1.05);
}

.descriptions-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The preview sheet and the descriptions sheet, same frame as the rest. */
.invoice-preview-modal,
.descriptions-modal {
  border: 1px solid #dde3ea;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.preview-header,
.descriptions-modal-header {
  padding: 12px 14px;
  border-bottom: 1px solid #dde3ea;
}

.preview-header h3,
.descriptions-modal-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.descriptions-modal-body {
  padding: 12px 14px;
}

.descriptions-modal-footer {
  padding: 10px 14px;
  gap: 8px;
  border-top: 1px solid #dde3ea;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-nav,
.btn-delete-selected {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-nav {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-nav:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}

.btn-delete-selected {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #e04545;
}

.btn-delete-selected:hover:not(:disabled) {
  background: #fff;
  border-color: #e04545;
  color: #e04545;
  box-shadow: none;
  transform: none;
}


/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── The switch, the chips and the hints ───────────────────────────────
 *
 * The switch was a solid blue pill with a white knob — the loudest mark in a
 * card of hairlines — and the alternative names sat in blue-on-blue chips
 * with an 18px typographic cross. All three are now what the rest of the
 * product uses: a rail with a handle, a quiet chip with a hairline, and a
 * hint one step smaller than the field it explains. */
.toggle {
  width: 30px;
  height: 17px;
}

.toggle-track {
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  box-shadow: none;
}

.toggle-track::before,
.toggle-track::after {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 2.5px;
  right: auto;
  top: 50%;
  margin-top: -4.5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: none;
}

.toggle-track::after {
  content: none;
}

.toggle input:checked + .toggle-track {
  background: transparent;
  border-color: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(13px);
}

.toggle:hover .toggle-track {
  border-color: var(--primary);
}

.toggle-text,
.toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

/* An alternative name on file: a quiet chip, not a blue badge. */
.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px 2px 8px;
  background: #fff;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.alias-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.alias-remove:hover {
  background: transparent;
  color: #e04545;
  transform: translateY(-1px);
}

.alias-tags {
  gap: 5px;
}

.alias-input {
  font-size: 13px;
}

/* What a field means, a step below the field itself. */
.field-hint {
  font-size: 11px;
  line-height: 1.4;
  color: #94a3b8;
}

.form-group {
  margin-bottom: 10px;
}

.form-group label {
  margin-bottom: 3px;
}

/* ── The last details ──────────────────────────────────────────────────
 *
 * The internal code wore a violet gradient pill with a shadow — a colour
 * from no part of this product; the two marks beside the mapping fields were
 * 32px bordered tiles; and the hints were set in italics. */
.internal-code-badge {
  padding: 1px 6px;
  background: #f4f9fd;
  border: 1px solid #dde3ea;
  border-radius: 4px;
  color: var(--primary, #0697e4);
  font-size: 11px;
  font-weight: 700;
  box-shadow: none;
  margin-left: 8px;
}

.input-with-button .btn-icon {
  width: 26px;
  height: 26px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
}

.input-with-button .btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  transform: translateY(-2px);
}

.input-with-button .btn-icon svg {
  width: 14px;
  height: 14px;
}

/* These open the list of mappings already saved against the code, so on a code
   that does not exist yet there is nothing to open. In the action colour it
   read as a live button that ignored the click; grey says it is not one yet,
   and the tooltip says when it will be. */
.input-with-button .btn-icon:disabled {
  color: #94a3b8;
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-nav {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  box-shadow: none;
}

.btn-nav:hover:not(:disabled) {
  background: transparent;
  color: var(--primary, #0697e4);
  transform: translateY(-2px);
}

.btn-nav:disabled {
  color: #cbd5e1;
  cursor: default;
}

.field-hint {
  font-style: normal;
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
}

.form-group-toggle-inline {
  margin-top: 6px;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>
